import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Modal, TableColumnsType } from "antd";
import { AnyObject } from "antd/lib/_util/type";
import { ColumnsType } from "antd/lib/table";

import { InventoryListParam, list } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";

import { CommonListParams } from "@/types/common";
import { InventoryV2 } from "@/types/inventoryV2";

import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { numFormmater } from "@/pages/Requirement/util";

interface InventorySelectProps {
  handleOk: (inventories: Array<InventoryV2>) => void;
  openInventory: boolean;
  setOpenInventory: Dispatch<SetStateAction<boolean>>;
}

const InventorySelectMulti: FC<InventorySelectProps> = ({ handleOk, openInventory, setOpenInventory }) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [inventories, total, listLoading, { fetchList, clearData }] = useDataList<InventoryV2, [CommonListParams<InventoryListParam>]>({ fetchApi: list });
  const [selectIds, setSelectIds] = useState<Array<string>>([]);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    if (openInventory) {
      fetchInventories(0, "");
    }
  }, [openInventory]);

  const fetchInventories = (offset: number, keyword: string) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList({
      body: { keyword: keyword.trim() },
      pagination: { offset, take },
    });
  };

  const reset = () => {
    setOpenInventory(false);
    setSelectIds([]);
    setKeyword("");
    clearData();
  };

  const columns: TableColumnsType<InventoryV2> = [
    {
      title: t("product.code"),
      dataIndex: "productCode",
      render: (_, inventory) => (
        <SText style={{ width: 100 }} text={inventory?.product?.code} />
      ),
    },
    {
      title: t("product.name"),
      dataIndex: "productName",
      render: (_, inventory) => (
        <SText style={{ width: 160 }} text={inventory?.product?.name} />
      ),
    },
    {
      title: t("product.brand1"),
      dataIndex: "productModel1",
      render: (_, inventory) => (
        <SText style={{ width: 300 }} text={inventory?.product?.model} />
      ),
    },
    {
      title: t("product.uom"),
      dataIndex: "productUom",
      render: (_, inventory) => <SText text={inventory?.product?.uom} />,
    },
    {
      title: t("order.quantity"),
      dataIndex: "count",
      width: 120,
      render: (_, inventory) => <SText text={numFormmater(inventory?.count ?? 0)} />,
    },
    {
      title: t("warehouse.preIn"),
      dataIndex: "preIn",
      width: 120,
      render: (_, inventory) => <SText text={numFormmater(inventory?.preIn ?? 0)} />,
    },
    {
      title: t("warehouse.preOut"),
      dataIndex: "preOut",
      width: 120,
      render: (_, inventory) => <SText text={numFormmater(inventory?.preOut ?? 0)} />,
    },
  ];

  return (
    <Modal
      centered
      open={openInventory}
      width={1000}
      onCancel={reset}
      title={t("warehouse.selectProduct")}
      onOk={() => {
        handleOk(inventories.filter(item => selectIds.includes(item.productId)));
        reset();
      }}
    >
      <div style={{ height: "600px" }}>
        <STableLayout
          loading={listLoading}
          dataSource={inventories}
          rowKey="productId"
          rowSelection={{
            type: "checkbox",
            fixed: true,
            selectedRowKeys: selectIds,
            onChange: (keys) => setSelectIds(keys as Array<string>),
          }}
          columns={columns as ColumnsType<AnyObject>}
          scrollX={1400}
          scroll={{ y: 480 }}
          headerChildren={
            <Input.Search
              enterButton
              placeholder={t("requirement.searchPro")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(e) => {
                fetchInventories(0, e);
                tableRef?.current?.resetTablePage();
              }}
            />
          }
          tableLayoutRef={tableRef}
          total={total}
          handleChange={(offset) => {
            fetchInventories(offset, keyword);
          }}
        />
      </div>
    </Modal>
  );
};

export default InventorySelectMulti;
